// src/constants/theme.js
// 앱 전체 디자인 토큰 — 단일 소스(Single Source of Truth)
// 새 화면 / 기존 화면 모두 색은 이 파일의 COLORS 만 참조하세요. (하드코딩 금지)
//
// 51차: 디자인 토큰 이원화 정리
//   - 기존 theme.js 값(#0A0A0F / 크림 텍스트 #F0ECE0 등)이 실제 화면에서 안 쓰이고,
//     화면들이 각자 팔레트(#0a0a14 / 순백 #fff / #d4a843)를 하드코딩하던 문제 해결
//   - 실제 렌더 기조를 정본(canonical)으로 채택 → 아래 PALETTE 가 유일한 진실
//   - 기존 키(bgPrimary, textPrimary, success 등)는 삭제하지 않고 canonical 별칭으로 유지
//     (이미 import 중인 화면이 깨지지 않도록. 단 값은 기조에 맞춰 정렬됨)
// 72차: claude.ai/design "UNLOCK 디자인" 프로젝트 반영 — 팔레트를 더 차분하고 채도 낮은
//   톤으로 정렬(gold #d4a843→#D8AE52, 카드 테두리는 골드 틴트 대신 중성 화이트 알파로
//   변경해 골드는 강조용으로만 남김), 본문 텍스트를 순백 대신 크림톤(#F4F1E9)으로,
//   Pretendard/Space Grotesk 실 폰트 로드(App.js), 카드 radius를 더 넉넉하게(22px),
//   그라디언트 버튼용 GRADIENTS 신규 export.
// 75차: 데드코드 정리 — FONT_SIZE/SPACING/COMMON/HEADER_HEIGHT 삭제(전부 호출부 없음,
// StatusBar import도 HEADER_HEIGHT 전용이라 같이 제거). RADIUS/SHADOW는 실사용 중이라 유지.

// ── 정본 팔레트 (canonical) ─────────────────────────────────────────────────
// 화면에서 쓰던 GOLD/BG/SURFACE/SURFACE2/TEXT... 이름과 1:1 매핑됨
const PALETTE = {
  // 골드 포인트 — 72차: UNLOCK 디자인 시스템 값으로 교체
  gold:        '#D8AE52',
  goldLight:   '#EFD292',   // 버튼/뱃지 위 밝은 골드 텍스트
  goldPale:    '#F6E6B8',   // 그라디언트 최상단(로고/버튼)
  goldDark:    '#A87C2E',   // 그라디언트 최하단
  goldGlow:    'rgba(216,174,82,0.30)',
  goldBg:      'rgba(216,174,82,0.10)',
  goldBgLight: 'rgba(216,174,82,0.15)',
  goldBorder:  'rgba(216,174,82,0.50)',

  // 배경/표면
  bg:        '#0a0a14',   // 화면 배경 (구 BG)
  surface:   '#14141f',   // 카드 배경 (구 SURFACE)
  surface2:  '#1e1e2c',   // 중첩/입체 요소 · "Raised" (구 SURFACE2)
  onGold:    '#241a06',   // 골드 버튼 위 텍스트(진한 브라운블랙 — 순검정보다 톤이 부드러움)
  // 78차: EscapeDashboardScreen.js에 하드코딩돼 있던 성공/실패 pill 위 텍스트 색을
  // onGold와 동일한 패턴(같은 색상의 진한 톤)으로 토큰화 — 색상 하드코딩 금지 컨벤션 정리
  onGreen:   '#0b2417',   // 성공(green) pill 위 텍스트
  onRed:     '#2a0f0c',   // 실패(red) pill 위 텍스트

  // 텍스트 — 72차: 순백 대신 크림톤, 보조 텍스트는 rgba 대신 고정 회색톤
  text:      '#F4F1E9',                 // 본문 (구 TEXT, 기존 #ffffff)
  textSec:   '#9A9AA8',                 // 보조 (구 TEXT_SEC, 기존 rgba(255,255,255,0.5))
  textMuted: '#7A7A88',                 // 흐림 (구 TEXT_MUTED, 기존 rgba(255,255,255,0.3))

  // 테두리 — 72차: 카드 테두리를 골드 틴트 → 중성 화이트 알파로 (골드는 강조/활성 상태 전용)
  border:       'rgba(255,255,255,0.08)',  // 기본 (구 BORDER, 기존 골드 0.15)
  borderActive: 'rgba(216,174,82,0.50)',   // 선택/활성 상태만 골드

  // 상태 색 (72차: 채도 낮춘 톤으로 정렬)
  green:   '#6FBF8E',
  red:     '#CD6F66',
  orange:  '#ff6b35',
  blue:    '#6E86C4',
  purple:  '#a78bfa',
  teal:    '#4ecdc4',
  warning: '#FBBF24',

  // 51차: 색 계열 단일화 — 초록/빨강/주황/파랑은 각 1종만 사용
  //   dangerSoft(구 연빨강)는 red 로 흡수 → 빨강 하나로 통일
  dangerSoft:       '#CD6F66',
  dangerSoftBg:     'rgba(205,111,102,0.12)',
  dangerSoftBorder: 'rgba(205,111,102,0.40)',

  // 상태 보더
  greenBorder: 'rgba(111,191,142,0.40)',
  redBorder:   'rgba(205,111,102,0.40)',

  // 상태 배경 틴트
  greenBg:   'rgba(111,191,142,0.12)',
  redBg:     'rgba(205,111,102,0.12)',
  orangeBg:  'rgba(255,107,53,0.12)',
  blueBg:    'rgba(110,134,196,0.12)',
  warningBg: 'rgba(251,191,36,0.12)',

  // 기타
  white:   '#ffffff',
  overlay: 'rgba(10,10,20,0.85)',

  // 75차: design_handoff_unlock 디자인 시스템 — BOOST 플랜 라인 컬러(공포 장르와 공용 coral),
  // BASIC 플랜 라인 컬러(중성 그레이, textSec/textMuted와는 별개의 정확한 스펙값)
  boost:     '#D98A6E',
  planBasic: '#8A8A98',
};

// ── 색상 export ─────────────────────────────────────────────────────────────
// canonical 키 + (하위호환) 기존 키를 canonical 값으로 별칭 처리
export const COLORS = {
  ...PALETTE,

  // ── DEPRECATED 별칭 (기존 import 보호용 — 신규 코드에서는 위 canonical 키 사용) ──
  bgPrimary:     PALETTE.bg,
  bgSecondary:   PALETTE.surface,
  bgCard:        PALETTE.surface2,
  bgCardHover:   '#26263a',

  textPrimary:   PALETTE.text,
  textSecondary: PALETTE.textSec,
  // textMuted 는 canonical 과 키 동일

  success:   PALETTE.green,
  successBg: PALETTE.greenBg,
  danger:    PALETTE.red,
  dangerBg:  PALETTE.redBg,
  info:      PALETTE.blue,
  infoBg:    PALETTE.blueBg,
  // warning / warningBg 는 canonical 과 키 동일

  borderSubtle: PALETTE.border,
  goldSubtle:   PALETTE.border,
};

// ── 그라디언트 (72차 신규) ────────────────────────────────────────────────────
// react-native에는 CSS linear-gradient가 없어 expo-linear-gradient로 적용.
// <LinearGradient colors={GRADIENTS.button} start={{x:0,y:0}} end={{x:1,y:1}} />
export const GRADIENTS = {
  // 로고/히어로 텍스트용 — 180deg, 밝은 골드→진한 골드
  logo:   [PALETTE.goldPale, PALETTE.gold, PALETTE.goldDark],
  // 기본 CTA 버튼용 — 135deg, 디자인 시스템 원본 값
  button: [PALETTE.goldLight, PALETTE.gold],
};

// ── 타이포그래피 ───────────────────────────────────────────────────────────────
// 72차: Pretendard(국문 본문) + Space Grotesk(순수 영문/숫자 전용) 실 폰트 로드.
// App.js의 useFonts()가 이 이름으로 폰트를 등록하므로 반드시 이름이 일치해야 함.
// ⚠️ FONTS.display는 기존에 "매장 요약"·"UNLOCK 기여도" 같은 국문 섞인 섹션 제목에
// 이미 쓰이고 있던 키다 — Space Grotesk는 한글 글리프가 아예 없는 라틴 전용 폰트라
// 여기 배정하면 기기별 글리프 폴백 동작에 기대야 해서 위험하다. 그래서 display는
// 안전하게 Pretendard 계열로 유지하고, 로고/숫자처럼 "라틴/숫자만 나온다고 확신하는
// 곳"에는 반드시 FONTS.brand*를 새로 골라 쓸 것.
export const FONTS = {
  display:       'Pretendard-Bold',         // 국문 섞인 섹션 제목 (기존 사용처 그대로 안전 유지)
  displaySemi:   'Pretendard-SemiBold',
  displayMedium: 'Pretendard-Medium',
  body:          'Pretendard-Regular',
  bodyMedium:    'Pretendard-Medium',
  bodySemiBold:  'Pretendard-SemiBold',
  bodyBold:      'Pretendard-Bold',
  // 라틴/숫자 전용(로고 워드마크, 가격 큰 숫자, 영문 브랜드 캡션) — 한글 텍스트에는 쓰지 말 것
  brand:         'SpaceGrotesk_700Bold',
  brandSemiBold: 'SpaceGrotesk_600SemiBold',
  brandMedium:   'SpaceGrotesk_500Medium',
};

// ── 모서리 — 72차: UNLOCK 디자인의 더 넉넉한 라운딩으로 조정 ───────────────────
export const RADIUS = {
  sm:   10,
  md:   14,
  lg:   22,
  xl:   28,
  pill: 999,
};

// ── 그림자 ────────────────────────────────────────────────────────────────────
export const SHADOW = {
  card: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.5,
    shadowRadius: 12,
    elevation: 8,
  },
  glow: {
    shadowColor: PALETTE.gold,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 4,
  },
  // 72차: 디자인 시스템의 골드 버튼 그림자(0 6px 18px rgba(216,174,82,0.25))
  button: {
    shadowColor: PALETTE.gold,
    shadowOffset: { width: 0, height: 6 },
    shadowOpacity: 0.25,
    shadowRadius: 18,
    elevation: 6,
  },
};

